import { useState } from "react"
import { ExternalLink, Link2, Pencil, Unlink } from "lucide-react"
import type { AppShell } from "@/App"
import type { CatalogKind, CrmProductSummary, MappingSearchRow } from "@/lib/api"
import { ProductSearch } from "./ProductSearch"

/**
 * Referência de uma linha do catálogo AC/BC, como no solar: ligada, o SKU é
 * o link para o produto no CRM (de onde o PDF tira foto e ficha técnica);
 * «trocar» e «desligar» ao lado. Sem ligação, «Associar produto do CRM» abre a
 * mesma pesquisa — o SKU vem sempre do CRM, nunca escrito à mão.
 */
export function ProductRefCell({
  shell,
  catalog,
  sku,
  crmProduct,
  productId,
  roleLabel,
  onLink,
  onOpenProduct,
}: {
  shell: AppShell | null
  catalog: CatalogKind
  sku: string | null
  crmProduct: CrmProductSummary | null
  productId: number | null
  roleLabel: (role: string) => string
  onLink: (productId: number | null) => void
  onOpenProduct?: (productId: number) => void
}) {
  const [picking, setPicking] = useState(false)
  const linkedId = crmProduct?.id ?? productId
  const missing = crmProduct
    ? [!crmProduct.has_image && "sem foto", !crmProduct.has_datasheet && "sem ficha"].filter(Boolean).join(" · ")
    : null

  const pick = (p: MappingSearchRow) => {
    setPicking(false)
    onLink(p.product_id)
  }

  return (
    <div className="space-y-1">
      {linkedId != null ? (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onOpenProduct?.(linkedId)}
            title={crmProduct ? `Abrir no CRM: ${crmProduct.name}` : "Abrir produto no CRM"}
            className="inline-flex min-w-0 items-center gap-1 font-mono text-xs text-accent hover:underline"
          >
            <span className="truncate">{sku || "ver produto"}</span>
            <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
          </button>
          <button type="button" onClick={() => setPicking((v) => !v)} title="Trocar o produto do CRM" className="text-muted-foreground hover:text-foreground">
            <Pencil className="h-3 w-3" />
          </button>
          <button type="button" onClick={() => onLink(null)} title="Desligar do produto do CRM" className="text-muted-foreground hover:text-destructive">
            <Unlink className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-accent hover:underline"
        >
          <Link2 className="h-3 w-3" />
          Associar produto do CRM
        </button>
      )}
      {crmProduct && missing && <div className="text-[11px] text-amber-600">{missing}</div>}
      {linkedId != null && !crmProduct && <div className="text-[11px] text-amber-600">produto não encontrado no CRM</div>}
      {picking && (
        <ProductSearch
          shell={shell}
          catalog={catalog}
          onPick={pick}
          autoFocus
          inline
          placeholder="Nome ou SKU do produto…"
          className="w-72"
          taken={(p) => (p.product_id === linkedId ? "ligado" : null)}
          hint={(p) => {
            const l = p.catalog_links?.[0]
            return l ? `já em ${roleLabel(l.role)} (#${l.id})` : null
          }}
        />
      )}
    </div>
  )
}
